import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import {
  X,
  CheckCircle2,
  Copy,
  ExternalLink,
  Download,
  QrCode,
  PlayCircle,
  Volume2,
  Sparkles,
} from 'lucide-react';
import { proxyImageUrl } from '../../../../utils/proxy';

export interface PublishPosterModalProps {
  isOpen: boolean;
  onClose: () => void;
  ossUrl: string;
  imgUrl?: string | null;
  audioUrl?: string | null;
  audioMsg?: string | null;
  videoUrl?: string | null;
  video1stUrl?: string | null;
  qrUrl?: string | null;
  fileName?: string;
  firstMessageContent?: string;
}

const LOGO_URL = import.meta.env.DEV ? '/imgs-proxy/logo.png' : 'https://imgs.aitcyc.com/logo.png';

const safeFilename = (value: string) =>
  value
    .replace(/[\\/:*?"<>|]/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 60);

const waitForImages = async (root: HTMLElement) => {
  const images = Array.from(root.querySelectorAll('img'));
  await Promise.all(
    images.map((img) => {
      if (img.complete) return Promise.resolve();
      return new Promise<void>((resolve) => {
        img.onload = () => resolve();
        img.onerror = () => resolve();
      });
    }),
  );
};

const assetUrl = (url?: string | null) => {
  if (!url) return '';
  try {
    return proxyImageUrl(url);
  } catch {
    return url;
  }
};

const PublishPosterModal: React.FC<PublishPosterModalProps> = ({
  isOpen,
  onClose,
  ossUrl,
  imgUrl,
  audioUrl,
  audioMsg,
  videoUrl,
  video1stUrl,
  qrUrl,
  fileName,
  firstMessageContent,
}) => {
  const posterRef = useRef<HTMLDivElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [exporting, setExporting] = useState(false);
  const [saveTip, setSaveTip] = useState('');
  const [copied, setCopied] = useState(false);
  const [playing, setPlaying] = useState(false);
  const autoSavedRef = useRef(false);

  const getUrlWithTimestamp = useCallback((url: string) => {
    if (!url) return '';
    const timestamp = Date.now();
    if (url.includes('?v=')) return url.replace(/\?v=\d+/, `?v=${timestamp}`);
    if (url.includes('?')) return `${url}&v=${timestamp}`;
    return `${url}?v=${timestamp}`;
  }, []);

  const publishUrl = useMemo(() => getUrlWithTimestamp(ossUrl), [getUrlWithTimestamp, ossUrl]);

  const workTitle = useMemo(() => {
    const raw = (fileName || '我的作品').replace(/\.[^/.]+$/, '');
    return raw.trim() || '我的作品';
  }, [fileName]);

  const inspirationText = useMemo(() => {
    return audioMsg?.trim() || firstMessageContent?.trim() || '快来看看我今天做好的作品吧！';
  }, [audioMsg, firstMessageContent]);

  const displayCoverUrl = useMemo(() => assetUrl(imgUrl), [imgUrl]);
  const displayQrUrl = useMemo(() => assetUrl(qrUrl), [qrUrl]);
  const displayVideo1stUrl = useMemo(() => assetUrl(video1stUrl), [video1stUrl]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(publishUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      alert('复制失败，请手动复制链接。');
    }
  }, [publishUrl]);

  const handleToggleAudio = useCallback(() => {
    if (!audioRef.current || !audioUrl) return;
    if (audioRef.current.paused) {
      audioRef.current.play().then(() => setPlaying(true)).catch(() => undefined);
    } else {
      audioRef.current.pause();
      setPlaying(false);
    }
  }, [audioUrl]);

  useEffect(() => {
    const current = audioRef.current;
    if (!current) return;
    const onEnded = () => setPlaying(false);
    const onPause = () => setPlaying(false);
    current.addEventListener('ended', onEnded);
    current.addEventListener('pause', onPause);
    return () => {
      current.removeEventListener('ended', onEnded);
      current.removeEventListener('pause', onPause);
    };
  }, [audioUrl]);

  const handleSavePoster = useCallback(
    async (auto = false) => {
      if (!posterRef.current || exporting) return;
      setExporting(true);
      setSaveTip(auto ? '正在自动保存长图海报...' : '正在保存长图海报...');
      try {
        await waitForImages(posterRef.current);
        await new Promise((resolve) => setTimeout(resolve, 120));
        const canvas = await html2canvas(posterRef.current, {
          backgroundColor: '#fffdfb',
          scale: Math.min(window.devicePixelRatio || 2, 2),
          useCORS: true,
          allowTaint: false,
          logging: false,
          windowWidth: posterRef.current.scrollWidth,
          windowHeight: posterRef.current.scrollHeight,
        });
        const link = document.createElement('a');
        link.href = canvas.toDataURL('image/png', 1);
        link.download = `${safeFilename(workTitle)}-作品海报.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setSaveTip(auto ? '已自动保存到本地；如未下载，请点击按钮手动保存。' : '长图海报已保存到本地。');
      } catch (error) {
        console.error(error);
        setSaveTip('保存失败，请检查 html2canvas 是否已安装，以及图片/视频资源是否支持跨域。');
      } finally {
        setExporting(false);
      }
    },
    [exporting, workTitle],
  );

  useEffect(() => {
    if (!isOpen) {
      autoSavedRef.current = false;
      setSaveTip('');
      setPlaying(false);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      return;
    }
    if (autoSavedRef.current) return;
    autoSavedRef.current = true;
    const timer = window.setTimeout(() => handleSavePoster(true), 900);
    return () => window.clearTimeout(timer);
  }, [isOpen, handleSavePoster]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/75 p-4 backdrop-blur-md">
      <div className="relative flex h-[94vh] w-full max-w-[1280px] flex-col overflow-hidden rounded-[32px] border border-white/15 bg-slate-950 shadow-[0_30px_120px_rgba(0,0,0,0.45)] lg:flex-row">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-20 rounded-full bg-white/10 p-2 text-white/80 transition hover:bg-white/20 hover:text-white"
        >
          <X size={18} />
        </button>

        <div className="min-h-0 flex-1 overflow-y-auto bg-[radial-gradient(circle_at_top,_rgba(251,191,36,0.12),_transparent_25%),radial-gradient(circle_at_left,_rgba(59,130,246,0.18),_transparent_32%),linear-gradient(180deg,#111827,#0b1220_55%,#111827)] p-3 sm:p-5">
          <div className="mx-auto w-full max-w-[860px] rounded-[36px] bg-white shadow-[0_25px_80px_rgba(44,18,64,0.18)]">
            <div
              ref={posterRef}
              className="relative overflow-hidden rounded-[36px] bg-[linear-gradient(180deg,#fff8fb_0%,#fffef9_35%,#f6fbff_100%)] text-slate-800"
            >
              <div className="pointer-events-none absolute -left-14 top-16 h-44 w-44 rounded-full bg-pink-300/40 blur-3xl" />
              <div className="pointer-events-none absolute right-0 top-0 h-56 w-56 rounded-full bg-cyan-300/35 blur-3xl" />
              <div className="pointer-events-none absolute bottom-20 left-0 h-56 w-56 rounded-full bg-yellow-200/40 blur-3xl" />
              <div className="pointer-events-none absolute bottom-10 right-0 h-56 w-56 rounded-full bg-violet-200/35 blur-3xl" />

              <div className="relative px-5 pb-8 pt-6 sm:px-8 sm:pb-10 sm:pt-8">
                <section className="overflow-hidden rounded-[30px] bg-[linear-gradient(135deg,#7c3aed_0%,#ec4899_45%,#f59e0b_100%)] p-5 text-white shadow-[0_18px_40px_rgba(124,58,237,0.28)] sm:p-6">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="inline-flex items-center gap-2 rounded-full bg-white/18 px-3 py-1.5 text-xs font-black tracking-[0.22em] backdrop-blur-sm sm:text-sm">
                        <CheckCircle2 size={16} className="text-lime-200" />
                        发布成功
                      </div>
                      <h2 className="mt-3 text-3xl font-black leading-tight sm:text-4xl">我的作品海报出炉啦</h2>
                      <p className="mt-2 text-sm font-semibold text-white/90 sm:text-base">扫码就能打开作品页，家里人一眼就能看到我今天的新作品。</p>
                    </div>
                    <div className="shrink-0 rounded-[20px] bg-white px-4 py-3 shadow-lg">
                      <img src={LOGO_URL} alt="童程优创" className="h-8 w-auto sm:h-10" crossOrigin="anonymous" />
                    </div>
                  </div>
                </section>

                <section className="mt-6 grid gap-5 lg:grid-cols-[1.18fr_0.82fr]">
                  <div className="rounded-[30px] border-4 border-white bg-white p-4 shadow-[0_16px_40px_rgba(244,114,182,0.16)]">
                    <div className="mb-3 flex items-center gap-2 text-pink-500">
                      <Sparkles size={18} />
                      <span className="text-sm font-black tracking-[0.18em]">作品封面</span>
                    </div>

                    <div className="mb-4 rounded-[22px] bg-[linear-gradient(135deg,#fff1f2,#ffffff)] p-4 shadow-inner">
                      <p className="text-xs font-black tracking-[0.16em] text-pink-500">我的作品名字</p>
                      <p className="mt-2 text-2xl font-black leading-tight text-slate-900">{workTitle}</p>
                    </div>

                    <div className="overflow-hidden rounded-[24px] bg-[linear-gradient(135deg,#fde68a,#fbcfe8,#bfdbfe)] p-2">
                      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[18px] bg-white/70">
                        {displayCoverUrl ? (
                          <img
                            src={displayCoverUrl}
                            alt="作品封面"
                            crossOrigin="anonymous"
                            className="absolute inset-0 h-full w-full object-contain"
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center text-center text-slate-500">
                            请上传作品封面图
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-5">
                    <div className="rounded-[30px] border-4 border-white bg-white p-4 shadow-[0_16px_40px_rgba(96,165,250,0.16)]">
                      <div className="mb-3 flex items-center gap-2 text-sky-500">
                        <PlayCircle size={18} />
                        <span className="text-sm font-black tracking-[0.18em]">出镜小视频</span>
                      </div>
                      <div className="overflow-hidden rounded-[24px] bg-[linear-gradient(135deg,#dbeafe,#ffffff,#fde68a)] p-2">
                        <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[18px] bg-slate-100">
                          {displayVideo1stUrl ? (
                            <img src={displayVideo1stUrl} alt="视频首帧" crossOrigin="anonymous" className="absolute inset-0 h-full w-full object-cover" />
                          ) : displayCoverUrl ? (
                            <img src={displayCoverUrl} alt="视频首帧占位" crossOrigin="anonymous" className="absolute inset-0 h-full w-full object-cover opacity-85" />
                          ) : (
                            <div className="flex h-full items-center justify-center text-center text-slate-500">暂无出镜视频</div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/35 via-transparent to-transparent" />
                          <div className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/88 shadow-lg">
                            <PlayCircle size={32} className="text-sky-500" />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-[30px] border-4 border-white bg-white p-4 shadow-[0_16px_40px_rgba(168,85,247,0.18)]">
                      <div className="mb-3 flex items-center gap-2 text-violet-500">
                        <QrCode size={18} />
                        <span className="text-sm font-black tracking-[0.18em]">扫码打开作品</span>
                      </div>
                      <div className="flex items-center justify-center rounded-[24px] bg-[linear-gradient(135deg,#eef6ff,#ffffff)] p-4">
                        {displayQrUrl ? (
                          <img
                            src={displayQrUrl}
                            alt="作品二维码"
                            crossOrigin="anonymous"
                            className="h-[220px] w-[220px] rounded-[20px] border border-slate-200 bg-white p-3 shadow-sm"
                          />
                        ) : (
                          <div className="flex h-[220px] w-[220px] items-center justify-center rounded-[20px] border border-dashed border-slate-300 bg-white text-center text-slate-500">
                            暂无二维码
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </section>

                <section className="mt-6 rounded-[34px] border-4 border-white bg-[linear-gradient(135deg,#fff7ed,#fff1f2_45%,#eef6ff)] p-5 shadow-[0_18px_42px_rgba(251,146,60,0.18)] sm:p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[linear-gradient(135deg,#f59e0b,#ec4899)] text-white shadow-lg">
                      <Volume2 size={24} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-black tracking-[0.18em] text-orange-500">我的创作灵感</p>
                      <p className="mt-2 text-[26px] font-black leading-[1.5] text-slate-900 sm:text-[30px]">“{inspirationText}”</p>
                      {audioUrl && (
                        <div className="mt-4 flex flex-wrap items-center gap-3">
                          <button
                            onClick={handleToggleAudio}
                            className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-3 text-sm font-bold text-white transition hover:bg-slate-800"
                          >
                            <Volume2 size={16} />
                            {playing ? '暂停孩子原声' : '播放孩子原声'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </section>

                <section className="mt-6 rounded-[30px] border-4 border-dashed border-pink-200 bg-white/90 p-5 shadow-[0_12px_34px_rgba(244,114,182,0.12)]">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="rounded-full bg-pink-100 px-4 py-2 text-sm font-black text-pink-600">网页链接</div>
                    <p className="min-w-0 flex-1 break-all text-sm font-semibold leading-6 text-slate-600">{publishUrl}</p>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>

        <aside className="w-full border-t border-white/10 bg-slate-950/90 p-5 text-white lg:w-[350px] lg:border-l lg:border-t-0">
          <div className="rounded-[28px] border border-white/10 bg-white/5 p-5 shadow-[0_20px_40px_rgba(0,0,0,0.25)]">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-300">
                <CheckCircle2 size={24} />
              </div>
              <div>
                <h3 className="text-xl font-black">发布成功</h3>
                <p className="mt-1 text-sm text-white/65">{fileName || '作品已生成海报长图'}</p>
              </div>
            </div>

            <div className="mt-5 grid gap-3">
              <button
                onClick={() => handleSavePoster(false)}
                disabled={exporting}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[linear-gradient(135deg,#a855f7,#ec4899)] px-4 py-3 text-sm font-black text-white shadow-[0_16px_30px_rgba(168,85,247,0.35)] transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Download size={16} />
                {exporting ? '保存中...' : '保存长图海报'}
              </button>

              <button
                onClick={handleCopy}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white/8 px-4 py-3 text-sm font-bold text-white transition hover:bg-white/12"
              >
                <Copy size={16} />
                {copied ? '已复制链接' : '复制作品链接'}
              </button>

              <a
                href={publishUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-4 py-3 text-sm font-black text-slate-900 transition hover:bg-white/90"
              >
                <ExternalLink size={16} />
                立即打开作品页
              </a>
            </div>

            {saveTip && (
              <div className="mt-4 rounded-2xl bg-white/6 px-4 py-3 text-sm leading-6 text-white/78">
                {saveTip}
              </div>
            )}
          </div>
        </aside>

        {audioUrl && <audio ref={audioRef} src={audioUrl} preload="none" />}
      </div>
    </div>
  );
};

export default PublishPosterModal;
